<template>
  <div class="flex flex-wrap">
    <div class="w-full px-4">
      <div
        class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded"
      >
        <div class="px-6 py-4 border-b border-blueGray-200">
          <h2 class="text-xl font-semibold text-blueGray-700">
            Resource Documents
          </h2>
          <p class="text-sm text-blueGray-500 mt-1">
            View and download helpful documents shared in your enrollment resources.
          </p>
        </div>

        <div v-if="loading" class="p-6 text-center text-blueGray-500">
          Loading resources...
        </div>

        <div v-else-if="resources.length === 0" class="p-6 text-center text-blueGray-500">
          No resource documents available yet.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th
                  class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600"
                >
                  Resource Name
                </th>
                <th
                  class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600"
                >
                  Resource Description
                </th>
                <th
                  class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600"
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="resource in normalizedResources"
                :key="resource.id"
                class="border-b border-gray-200"
              >
                <td class="px-6 align-middle py-4 text-sm text-left font-medium text-blueGray-700">
                  {{ resource.resource_name || "Untitled Resource" }}
                </td>

                <td class="px-6 align-middle py-4 text-sm text-left text-blueGray-600">
                  {{ resource.description || "-" }}
                </td>

                <td class="px-6 align-middle py-4 whitespace-nowrap">
                  <button
                    type="button"
                    @click="previewResource(resource.url, resource.resource_name)"
                    class="text-white text-xs font-bold px-4 py-2 rounded shadow hover:shadow-md mr-2 mb-1 ease-linear transition-all duration-150"
                    style="background-color: #3b82f6;"
                  >
                    <i class="fas fa-eye mr-1"></i> View
                  </button>

                  <button
                    type="button"
                    @click="downloadResource(resource.url, resource.resource_name)"
                    class="text-white text-xs font-bold px-4 py-2 rounded shadow hover:shadow-md mr-1 mb-1 ease-linear transition-all duration-150"
                    style="background-color: #10b981;"
                  >
                    <i class="fas fa-download mr-1"></i> Download
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/dist/sweetalert2.min.css";

export default {
  data() {
    return {
      resources: [],
      loading: false,
    };
  },

  computed: {
    normalizedResources() {
      return this.resources.map((resource) => {
        let cleanUrl = resource.url || "";

        // Support old bad format where multiple URLs may have been stored like:
        // url1|url2|url3
        if (cleanUrl && cleanUrl.includes("|")) {
          cleanUrl = cleanUrl.split("|")[0];
        }

        return {
          ...resource,
          url: cleanUrl,
        };
      });
    },
  },

  methods: {
    async fetchResources() {
      this.loading = true;

      const token = localStorage.getItem("token");

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_resources`,
          {
            params: { token },
          }
        );

        this.resources = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
      } catch (error) {
        console.error("Backend error while fetching resources:", error);

        Swal.fire({
          icon: "error",
          title: "Could not load resources",
          text: "Please try again later.",
        });

        this.resources = [];
      } finally {
        this.loading = false;
      }
    },

    getFileNameFromUrl(url) {
      if (!url) return "download";
      const cleanUrl = String(url).split("?")[0];
      const parts = cleanUrl.split("/");
      return parts[parts.length - 1] || "download";
    },

    fileExtension(nameOrUrl) {
      if (!nameOrUrl) return "";
      const base = String(nameOrUrl).split("?")[0];
      const parts = base.split(".");
      return parts.length > 1 ? parts.pop().toLowerCase() : "";
    },

    async previewResource(url, displayName) {
      if (!url) {
        Swal.fire({
          icon: "warning",
          title: "No file available",
          text: "This resource does not have a valid file URL.",
        });
        return;
      }

      const ext = this.fileExtension(displayName) || this.fileExtension(url);
      const imgTypes = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];

      Swal.fire({
        title: "Loading preview...",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      let blobUrl = null;

      try {
        const response = await axios.get(url, {
          responseType: "blob",
        });

        blobUrl = window.URL.createObjectURL(response.data);

        let html = "";

        if (imgTypes.includes(ext)) {
          html = `
            <img
              src="${blobUrl}"
              style="max-width:100%;max-height:70vh;object-fit:contain;"
              alt="Preview"
            />
          `;
        } else if (ext === "pdf") {
          html = `
            <iframe
              src="${blobUrl}"
              style="width:100%;height:70vh;border:0;"
              title="PDF preview"
            ></iframe>
          `;
        } else {
          html = `
            <div style="text-align:left;">
              <p style="margin-bottom:12px;color:#475569;">
                No in-browser preview for <strong>.${ext || "this file type"}</strong>.
              </p>
              <a
                href="${blobUrl}"
                target="_blank"
                rel="noopener noreferrer"
                style="display:inline-block;background:#3b82f6;color:#fff;padding:10px 16px;border-radius:6px;text-decoration:none;font-weight:600;"
              >
                Open in new tab
              </a>
            </div>
          `;
        }

        Swal.close();

        await Swal.fire({
          title: displayName || "Preview",
          html,
          width: "85%",
          showCloseButton: true,
          showConfirmButton: false,
          didClose: () => {
            if (blobUrl) {
              window.URL.revokeObjectURL(blobUrl);
            }
          },
        });
      } catch (error) {
        console.error("Preview error:", error);

        Swal.close();

        Swal.fire({
          icon: "error",
          title: "Preview failed",
          text: "Could not load this file. Please try downloading it instead.",
        });

        if (blobUrl) {
          window.URL.revokeObjectURL(blobUrl);
        }
      }
    },

    async downloadResource(url, fileName) {
      if (!url) {
        Swal.fire({
          icon: "warning",
          title: "No file available",
          text: "This resource does not have a valid file URL.",
        });
        return;
      }

      try {
        const response = await axios.get(url, {
          responseType: "blob",
        });

        this.downloadBlob(
          response.data,
          fileName || this.getFileNameFromUrl(url)
        );
      } catch (error) {
        console.error("Download error:", error);

        Swal.fire({
          icon: "error",
          title: "Download failed",
          text: "Could not download this file.",
        });
      }
    },

    downloadBlob(blob, fileName) {
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = blobUrl;
      a.download = fileName || "download";

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(blobUrl);
    },
  },

  mounted() {
    this.fetchResources();
  },
};
</script>
